/**
 * Owner: Masa Bayu
 * File: provenance.ts
 * Date:Time: 2026-09-18 15:50
 * Description: Source provenance for every extracted IR node.
 */

import { isQXDocumentSourceType, type QXDocumentSourceType } from './codes';
import { assertOptionalTrimmed, isRecord } from './guards';

export const QX_DOCUMENT_PROVENANCE_ALLOWED_KEYS = [
  'sourceType',
  'sourcePage',
  'sourceObjectId',
  'bbox',
  'adapterVersion',
] as const;

export interface QXDocumentProvenance {
  readonly sourceType: QXDocumentSourceType;
  readonly sourcePage: number | null;
  readonly sourceObjectId: string | null;
  readonly bbox: readonly number[] | null;
  readonly adapterVersion: string | null;
}

export interface CreateQXDocumentProvenanceOptions {
  readonly sourceType: QXDocumentSourceType;
  readonly sourcePage?: number | null;
  readonly sourceObjectId?: string | null;
  readonly bbox?: readonly number[] | null;
  readonly adapterVersion?: string | null;
}

function assertPage(value: unknown, label: string): number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    throw new Error(`${label} must be a positive integer`);
  }
  return value;
}

function assertBbox(value: unknown, label: string): readonly number[] | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (!Array.isArray(value) || value.length !== 4) {
    throw new Error(`${label} must be a 4-number bbox`);
  }
  const numbers = value.map((entry) => {
    if (typeof entry !== 'number' || Number.isNaN(entry)) {
      throw new Error(`${label} must contain only numbers`);
    }
    return entry;
  });
  return Object.freeze(numbers);
}

export function createQXDocumentProvenance(
  options: CreateQXDocumentProvenanceOptions,
): QXDocumentProvenance {
  if (!isRecord(options)) {
    throw new Error('Document provenance must be an object');
  }
  if (!isQXDocumentSourceType(options.sourceType)) {
    throw new Error('Document provenance sourceType is unknown');
  }
  return Object.freeze({
    sourceType: options.sourceType,
    sourcePage: assertPage(options.sourcePage, 'provenance.sourcePage'),
    sourceObjectId: assertOptionalTrimmed(options.sourceObjectId, 'provenance.sourceObjectId'),
    bbox: assertBbox(options.bbox, 'provenance.bbox'),
    adapterVersion: assertOptionalTrimmed(options.adapterVersion, 'provenance.adapterVersion'),
  });
}
